import React, { useState, useRef } from 'react';
import api from '../../services/api';

function BannerSucesso({ mensagem }) {
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
      <svg className="w-4 h-4 text-green-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
      <p className="text-sm text-green-700">{mensagem}</p>
    </div>
  );
}

function BannerErro({ mensagem }) {
  if (!mensagem) return null;
  return (
    <div className="flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg">
      <svg className="w-4 h-4 text-red-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="text-sm text-red-700">{mensagem}</p>
    </div>
  );
}

function inicialNome(nomeCompleto) {
  if (!nomeCompleto) return '?';
  const partes = nomeCompleto.trim().split(' ').filter(Boolean);
  if (partes.length === 1) return partes[0][0].toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

function CardUsuario({ usuario, onboardingId, readOnly, atualizarDados }) {
  const [editando, setEditando] = useState(false);
  const [nomeCompleto, setNomeCompleto] = useState(usuario.nomeCompleto || '');
  const [email, setEmail] = useState(usuario.email || '');
  const [telefone, setTelefone] = useState(usuario.telefone || '');
  const [observacao, setObservacao] = useState(usuario.observacao || '');
  const [salvando, setSalvando] = useState(false);
  const [excluindo, setExcluindo] = useState(false);
  const [erro, setErro] = useState('');

  function cancelarEdicao() {
    setNomeCompleto(usuario.nomeCompleto || '');
    setEmail(usuario.email || '');
    setTelefone(usuario.telefone || '');
    setObservacao(usuario.observacao || '');
    setErro('');
    setEditando(false);
  }

  async function handleSalvar(e) {
    e.preventDefault();
    if (!nomeCompleto.trim()) {
      setErro('O nome completo é obrigatório.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      await api.put(`/onboardings/${onboardingId}/usuarios/${usuario.id}`, {
        nomeCompleto,
        email,
        telefone,
        observacao,
      });
      await atualizarDados();
      setEditando(false);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao salvar. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  async function handleExcluir() {
    if (!window.confirm(`Deseja excluir o usuário "${usuario.nomeCompleto}"? Esta ação não pode ser desfeita.`)) return;
    setExcluindo(true);
    try {
      await api.delete(`/onboardings/${onboardingId}/usuarios/${usuario.id}`);
      await atualizarDados();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao excluir. Tente novamente.');
      setExcluindo(false);
    }
  }

  return (
    <div className="card space-y-4">
      {/* Cabeçalho do card */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-brand-700">{inicialNome(usuario.nomeCompleto)}</span>
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-gray-900 truncate">{usuario.nomeCompleto || '—'}</p>
            <p className="text-xs text-gray-400 truncate">{usuario.email || 'Sem e-mail informado'}</p>
          </div>
        </div>

        {!readOnly && !editando && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setEditando(true)}
              className="btn-ghost text-xs px-3 py-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Editar
            </button>
            <button
              type="button"
              onClick={handleExcluir}
              disabled={excluindo}
              className="btn-danger text-xs px-3 py-1.5"
            >
              {excluindo ? 'Excluindo...' : 'Excluir'}
            </button>
          </div>
        )}
      </div>

      {/* Dados detalhados (modo visualização) */}
      {!editando && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
          {usuario.telefone && (
            <div>
              <p className="text-xs text-gray-400 mb-0.5">Telefone</p>
              <p className="text-sm text-gray-700">{usuario.telefone}</p>
            </div>
          )}
          {usuario.observacao && (
            <div className={usuario.telefone ? '' : 'sm:col-span-2'}>
              <p className="text-xs text-gray-400 mb-0.5">Observação / Permissões</p>
              <p className="text-sm text-gray-700 whitespace-pre-line">{usuario.observacao}</p>
            </div>
          )}
        </div>
      )}

      {/* Formulário de edição inline */}
      {editando && (
        <form onSubmit={handleSalvar} className="space-y-4 border-t border-gray-100 pt-4">
          {erro && <BannerErro mensagem={erro} />}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="label">Nome Completo *</label>
              <input
                type="text"
                value={nomeCompleto}
                onChange={e => setNomeCompleto(e.target.value)}
                className="input"
                placeholder="Nome completo do usuário"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="label">E-mail</label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="input"
                placeholder="email@empresa.com.br"
              />
            </div>
            <div>
              <label className="label">Telefone</label>
              <input
                type="text"
                value={telefone}
                onChange={e => setTelefone(e.target.value)}
                className="input"
                placeholder="(11) 99999-9999"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Observação / Permissões</label>
              <textarea
                value={observacao}
                onChange={e => setObservacao(e.target.value)}
                className="input resize-none"
                rows={3}
                placeholder="Descreva o papel ou permissões deste usuário no sistema (opcional)"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={cancelarEdicao}
              className="btn-ghost text-xs"
            >
              Cancelar
            </button>
            <button type="submit" disabled={salvando} className="btn-primary text-xs">
              {salvando ? 'Salvando...' : 'Salvar alterações'}
            </button>
          </div>
        </form>
      )}

      {erro && !editando && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-xs text-red-700">{erro}</p>
        </div>
      )}
    </div>
  );
}

function FormAdicionarUsuario({ onboardingId, atualizarDados, onCancelar }) {
  const [nomeCompleto, setNomeCompleto] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [observacao, setObservacao] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');

  async function handleAdicionarUsuario(e) {
    e.preventDefault();
    if (!nomeCompleto.trim()) {
      setErro('O nome completo é obrigatório.');
      return;
    }
    setSalvando(true);
    setErro('');
    try {
      await api.post(`/onboardings/${onboardingId}/usuarios`, {
        nomeCompleto,
        email,
        telefone,
        observacao,
      });
      await atualizarDados();
      // Limpa e fecha
      setNomeCompleto('');
      setEmail('');
      setTelefone('');
      setObservacao('');
      if (onCancelar) onCancelar();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao adicionar usuário. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="card">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Adicionar Usuário</h3>

      <form onSubmit={handleAdicionarUsuario} className="space-y-4">
        {erro && <BannerErro mensagem={erro} />}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="label">Nome Completo *</label>
            <input
              type="text"
              value={nomeCompleto}
              onChange={e => setNomeCompleto(e.target.value)}
              className="input"
              placeholder="Nome completo do usuário"
              required
              autoFocus
            />
          </div>
          <div>
            <label className="label">E-mail</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              className="input"
              placeholder="email@empresa.com.br"
            />
          </div>
          <div>
            <label className="label">Telefone</label>
            <input
              type="text"
              value={telefone}
              onChange={e => setTelefone(e.target.value)}
              className="input"
              placeholder="(11) 99999-9999"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label">Observação / Permissões</label>
            <textarea
              value={observacao}
              onChange={e => setObservacao(e.target.value)}
              className="input resize-none"
              rows={3}
              placeholder="Descreva o papel ou permissões deste usuário no sistema (opcional)"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-1">
          {onCancelar && (
            <button type="button" onClick={onCancelar} className="btn-ghost text-xs">
              Cancelar
            </button>
          )}
          <button type="submit" disabled={salvando} className="btn-primary">
            {salvando ? 'Adicionando...' : 'Adicionar Usuário'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Usuarios({ dados, onboardingId, atualizarDados }) {
  const readOnly = !!dados.finalizado;
  const usuarios = dados.usuarios || [];
  const [exibirFormulario, setExibirFormulario] = useState(false);
  const timerSucesso = useRef(null);
  const [sucesso, setSucesso] = useState('');

  function aposAdicionar() {
    setExibirFormulario(false);
    setSucesso('Usuário adicionado com sucesso.');
    if (timerSucesso.current) clearTimeout(timerSucesso.current);
    timerSucesso.current = setTimeout(() => setSucesso(''), 4000);
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="section-title">Usuários do Sistema</h1>
          <p className="section-sub">
            Cadastre os usuários que terão acesso ao sistema da HeadThink.
          </p>
        </div>
        {!readOnly && !exibirFormulario && (
          <button
            type="button"
            onClick={() => setExibirFormulario(true)}
            className="btn-primary shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Adicionar
          </button>
        )}
      </div>

      {sucesso && <BannerSucesso mensagem={sucesso} />}

      {/* Formulário de adicionar no topo */}
      {!readOnly && exibirFormulario && (
        <FormAdicionarUsuario
          onboardingId={onboardingId}
          atualizarDados={() => { atualizarDados(); aposAdicionar(); }}
          onCancelar={() => setExibirFormulario(false)}
        />
      )}

      {/* Lista de usuários */}
      {usuarios.length === 0 ? (
        <div className="card text-center py-14 space-y-3">
          <div className="text-4xl">👤</div>
          <p className="font-semibold text-gray-700">Nenhum usuário cadastrado</p>
          <p className="text-sm text-gray-400 max-w-sm mx-auto">
            Adicione os usuários que utilizarão o sistema da HeadThink na sua empresa.
          </p>
          {!readOnly && !exibirFormulario && (
            <button
              type="button"
              onClick={() => setExibirFormulario(true)}
              className="btn-secondary mx-auto"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Adicionar primeiro usuário
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {usuarios.map(usuario => (
            <CardUsuario
              key={usuario.id}
              usuario={usuario}
              onboardingId={onboardingId}
              readOnly={readOnly}
              atualizarDados={atualizarDados}
            />
          ))}
        </div>
      )}

      <div className="pb-8" />
    </div>
  );
}
